import React, { useEffect, useRef, useState } from "react";
import paper from "paper";

import "./Room.scss";

interface RoomProps {
  onEnter: () => void;
  onLeave: () => void;
  onMoveTo: (x: number, y: number, noiseCancel: boolean) => void;
}
export const Room: React.FC<RoomProps> = ({ onEnter, onLeave, onMoveTo }) => {
  let canvasRef = useRef<HTMLCanvasElement>(null);
  let [pressed, setPressed] = useState(false);

  useEffect(() => {
    paper.setup(canvasRef.current!);

    let tool = new paper.Tool();
    let mouseDown = false;

    canvasRef.current!.addEventListener("mouseenter", () => onEnter());
    canvasRef.current!.addEventListener("mouseleave", () => onLeave());

    tool.onMouseMove = (evt: paper.ToolEvent) => {
      let pos = evt.point;
      let x = pos.x / paper.view.viewSize.width;
      let y = pos.y / paper.view.viewSize.height;

      let hit =
        !mouseDown &&
        paper.project.hitTest(evt.point, {
          segments: true,
          stroke: true,
          fill: true,
          tolerance: 5,
        });
      onMoveTo(x, y, !!hit);
    };

    let path = new paper.Path();
    path.strokeColor = new paper.Color("rgba(146, 146, 146, 1)");

    tool.onMouseDown = () => {
      mouseDown = true;
    };

    tool.onMouseDrag = (evt: paper.ToolEvent) => {
      path.add(evt.point);
    };

    tool.onMouseUp = () => {
      if (path.segments.length > 0) {
        let bubble = path;
        bubble.closePath();
        for (let i = 0; i < 100; i++) {
          bubble.divideAt((i / 100) * bubble.length);
        }
        bubble.fillColor = new paper.Color("rgba(146, 146, 146, 1)");

        // Construct circle to morph into
        let bubbleCircumference =
          (bubble.bounds.width + bubble.bounds.height) / 2;
        let circleRadius = bubbleCircumference / 2;
        let circ = new paper.Path(
          bubble.segments.map((segment) => {
            let vX = segment.point.x - bubble.position.x;
            let vY = segment.point.y - bubble.position.y;
            let magV = Math.sqrt(vX * vX + vY * vY);
            let aX = bubble.position.x + (vX / magV) * circleRadius;
            let aY = bubble.position.y + (vY / magV) * circleRadius;
            return new paper.Segment(new paper.Point(aX, aY));
          })
        );
        circ.remove();

        // Animate (partial) morphing into circle
        let morphedShape = bubble.clone();
        morphedShape.data.isBubble = true;
        bubble.remove();
        let duration = 1000;
        let startedAt = Date.now();
        let frame = () => {
          let elapsed = Date.now() - startedAt;
          morphedShape.interpolate(
            bubble,
            circ,
            0.9 * easeOutElastic(elapsed / duration)
          );
          if (elapsed < duration) {
            requestAnimationFrame(frame);
          } else {
            // Merge with existing paths if they intersect
            mergePathToOthers(morphedShape);
          }
        };
        requestAnimationFrame(frame);
      }

      // Initialise next path to draw
      path = new paper.Path();
      path.strokeColor = new paper.Color("rgba(146, 146, 146, 1)");
      mouseDown = false;
    };

    return () => {
      tool.remove();
    };
  }, [onMoveTo, onEnter, onLeave]);

  return <canvas
    ref={canvasRef}
    className={`room ${pressed ? 'pressed' : ''}`}
    onPointerDown={() => setPressed(true)}
    onPointerUp={() => setPressed(false)} />;
};

function mergePathToOthers(path: paper.Path) {
  for (let other of paper.project.getItems({
    class: paper.Path,
  }) as paper.Path[]) {
    if (other === path || !other.data.isBubble || !other.isInserted) continue;
    let union = other.unite(path);
    if (union instanceof paper.CompoundPath) {
      union.remove();
    } else {
      path.remove();
      other.remove();
      union.data.isBubble = true;
      mergePathToOthers(union as paper.Path);
      break;
    }
  }
}

function easeOutElastic(x: number): number {
  const c4 = (2 * Math.PI) / 3;

  return x === 0
    ? 0
    : x === 1
    ? 1
    : Math.pow(2, -10 * x) * Math.sin((x * 10 - 0.75) * c4) + 1;
}
